import { NavLink, Stack } from "@mantine/core";
import { IconChevronRight, IconSettings } from "@tabler/icons-react";
import { useState, cloneElement, isValidElement } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "store";
import { hasPermission } from "utils/permissions.utils";

export interface NavigationMenuStruct {
  label: string;
  link?: string;
  icon?: JSX.Element;
  subMenus?: NavigationMenuStruct[];
  privateAccess?: string[];
}

interface NavigationMenuProps {
  afterNavigate?: () => void;
}

export default function NavigationMenu({
  label,
  link,
  icon,
  subMenus,
  privateAccess,
  afterNavigate,
}: NavigationMenuStruct & NavigationMenuProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);

  // Helper to check if current path matches this menu or any submenu
  function isMenuActive(
    link?: string,
    subMenus?: NavigationMenuStruct[]
  ): boolean {
    if (link && location.pathname === link) return true;
    if (subMenus) {
      return subMenus.some(
        (menu) =>
          (menu.link && location.pathname === menu.link) ||
          (menu.subMenus && isMenuActive(menu.link, menu.subMenus))
      );
    }
    return false;
  }

  const [isOpen, setOpen] = useState(() =>
    subMenus?.length ? isMenuActive(link, subMenus) : false
  );

  if (privateAccess?.length && !hasPermission(user, privateAccess)) {
    return null;
  }
  function handleClick(): void {
    if (subMenus?.length) {
      setOpen((prev) => !prev);
    } else {
      if (link) {
        navigate(link);
        if (afterNavigate) afterNavigate();
      }
    }
  }

  return (
    <>
      <NavLink
        label={label}
        leftSection={
          isValidElement(icon)
            ? cloneElement<any>(icon, {
                style: { fontSize: "1rem" },
              })
            : undefined
        }
        rightSection={
          subMenus?.length ? (
            <IconChevronRight
              style={{
                transform: isOpen ? "rotate(90deg)" : undefined,
                transition: "transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              size={"1rem"}
            />
          ) : undefined
        }
        active={!subMenus?.length && location.pathname === link}
        onClick={() => handleClick()}
      />
      {subMenus?.length && isOpen && (
        <Stack gap="xs" pl="md">
          {subMenus.map((menu, mIndex) => (
            <NavigationMenu
              {...menu}
              afterNavigate={afterNavigate}
              key={mIndex}
            />
          ))}
        </Stack>
      )}
    </>
  );
}
